import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Trash2, Minus, Plus } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Cart = () => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    fetchCart();
  }, []);

  const fetchCart = async () => {
    try {
      const sessionId = localStorage.getItem('sessionId');
      if (!sessionId) {
        setLoading(false);
        return;
      }
      const response = await axios.get(`${API}/cart/${sessionId}`);
      setCartItems(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (itemId, newQuantity) => {
    if (newQuantity < 1) return;

    try {
      await axios.put(`${API}/cart/${itemId}`, { quantity: newQuantity });
      setCartItems(cartItems.map(item => 
        item.id === itemId ? { ...item, quantity: newQuantity } : item
      ));
      window.dispatchEvent(new Event('cartUpdated'));
    } catch (error) {
      console.error('Error updating quantity:', error);
      toast.error('Failed to update quantity');
    }
  };

  const removeItem = async (itemId) => {
    try {
      await axios.delete(`${API}/cart/${itemId}`);
      setCartItems(cartItems.filter(item => item.id !== itemId));
      toast.success('Item removed from cart');
      window.dispatchEvent(new Event('cartUpdated'));
    } catch (error) {
      console.error('Error removing item:', error);
      toast.error('Failed to remove item');
    }
  };

  const getTotal = () => {
    return cartItems.reduce((sum, item) => sum + (item.product_price * item.quantity), 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="loading-indicator">
        <p className="text-[#5D5C56]">Loading cart...</p>
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen py-24 px-6" data-testid="empty-cart">
        <div className="max-w-2xl mx-auto text-center">
          <h1 className="text-4xl font-heading font-bold text-[#1F2937] mb-4">Your Cart is Empty</h1>
          <p className="text-lg text-[#5D5C56] mb-8">Add some amazing shoes to your cart!</p>
          <Link to="/shop" data-testid="continue-shopping-link">
            <button className="btn-primary">Continue Shopping</button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-24 px-6 md:px-12 lg:px-24" data-testid="cart-page">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-5xl font-heading font-bold text-[#1F2937] mb-12" data-testid="cart-heading">
          Shopping Cart
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-6" data-testid="cart-items">
            {cartItems.map(item => (
              <div
                key={item.id}
                className="bg-white border border-[#E5E4DE] p-6 flex gap-6"
                data-testid={`cart-item-${item.id}`}
              >
                <img
                  src={item.product_image}
                  alt={item.product_name}
                  className="w-24 h-24 object-cover bg-[#F5F5F0]"
                  data-testid="cart-item-image"
                />
                <div className="flex-1">
                  <h3 className="text-xl font-heading font-semibold text-[#1F2937] mb-1" data-testid="cart-item-name">
                    {item.product_name}
                  </h3>
                  <p className="text-sm text-[#5D5C56] mb-2" data-testid="cart-item-brand">
                    {item.product_brand}
                  </p>
                  <p className="text-sm text-[#5D5C56]" data-testid="cart-item-details">
                    Size: {item.size} | Color: {item.color}
                  </p>
                </div>
                <div className="flex flex-col items-end justify-between">
                  <p className="text-xl font-semibold text-[#3E2723]" data-testid="cart-item-price">
                    ${(item.product_price * item.quantity).toFixed(2)}
                  </p>
                  <div className="flex items-center gap-2" data-testid="cart-item-quantity">
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      className="w-8 h-8 border border-[#E5E4DE] flex items-center justify-center hover:border-[#3E2723]"
                      data-testid="cart-item-decrease"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="w-8 text-center" data-testid="cart-item-quantity-value">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      className="w-8 h-8 border border-[#E5E4DE] flex items-center justify-center hover:border-[#3E2723]"
                      data-testid="cart-item-increase"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  <button
                    onClick={() => removeItem(item.id)}
                    className="text-red-600 hover:text-red-700 text-sm flex items-center gap-1"
                    data-testid="cart-item-remove"
                  >
                    <Trash2 className="w-4 h-4" />
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="lg:col-span-1" data-testid="cart-summary">
            <div className="bg-[#F5F5F0] p-8 sticky top-24">
              <h2 className="text-2xl font-heading font-semibold text-[#1F2937] mb-6" data-testid="summary-heading">
                Order Summary
              </h2>
              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-base" data-testid="summary-subtotal">
                  <span className="text-[#5D5C56]">Subtotal</span>
                  <span className="font-medium text-[#1F2937]">${getTotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-base" data-testid="summary-shipping">
                  <span className="text-[#5D5C56]">Shipping</span>
                  <span className="font-medium text-[#2E4A3B]">Free</span>
                </div>
                <div className="border-t border-[#E5E4DE] pt-4">
                  <div className="flex justify-between text-xl font-semibold" data-testid="summary-total">
                    <span className="text-[#1F2937]">Total</span>
                    <span className="text-[#3E2723]">${getTotal().toFixed(2)}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => navigate('/checkout')}
                className="btn-primary w-full"
                data-testid="checkout-button"
              >
                Proceed to Checkout
              </button>
              <Link to="/shop" data-testid="continue-shopping-button">
                <button className="btn-outline w-full mt-4">
                  Continue Shopping
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;